import React, { useCallback, useEffect, useMemo, useState } from 'react'

import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'

import { fees, constants } from '@blocktickets/shared-utils'
import {
  getOrganizationFeeConfig,
  updateOrganizationFeeConfig,
} from '../../utilities/api'
import AuthService from '../../utilities/services/auth.service'
import { capitalizeString } from '../../utilities/helpers'

import { LoadingContainer } from '../LoadingContainer'
import '../BuyerTypesWrapper/buyerTypesWrapper.scss'

const { FeeRuleType, FeeCategory, FeeCalculationMethod } = fees
const {
  ORDER_SOURCE,
  ORDER_SOURCE_LABEL,
  PAYMENT_TYPE,
  PAYMENT_TYPE_LABEL,
  PRODUCT_TYPE,
  PRODUCT_TYPE_LABEL,
} = constants

const CATEGORY_OPTIONS = Object.values(FeeCategory).map((value) => ({
  value,
  label: capitalizeString(value.replace(/_/g, ' ')),
}))

const TYPE_OPTIONS = Object.values(FeeRuleType).map((value) => ({
  value,
  label: capitalizeString(value.replace(/_/g, ' ')),
}))

const METHOD_OPTIONS = Object.values(FeeCalculationMethod).map((value) => ({
  value,
  label: capitalizeString(value),
}))

const SOURCE_OPTIONS = Object.values(ORDER_SOURCE).map((value) => ({
  value,
  label: ORDER_SOURCE_LABEL[value] || capitalizeString(value),
}))

const PAYMENT_METHOD_OPTIONS = Object.values(PAYMENT_TYPE).map((value) => ({
  value,
  label: PAYMENT_TYPE_LABEL[value] || capitalizeString(value),
}))

const PRODUCT_TYPE_OPTIONS = Object.values(PRODUCT_TYPE).map((value) => ({
  value,
  label: PRODUCT_TYPE_LABEL[value] || capitalizeString(value),
}))

const EMPTY_RULE_FORM = {
  name: '',
  category: FeeCategory.SERVICE,
  type: FeeRuleType.FLAT,
  method: FeeCalculationMethod.FIXED,
  value: '',
  maxPrice: '',
  maxQuantity: '',
  source: '',
  paymentMethod: '',
  productType: '',
  exclusive: true,
}

function PencilIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function formatRuleValue(rule) {
  const value = Number(rule?.value ?? 0)
  if (rule?.method === FeeCalculationMethod.PERCENTAGE) {
    return `${value}%`
  }
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

function formatRuleConstraints(rule) {
  const parts = []

  if (
    rule?.maxPrice !== undefined &&
    rule?.maxPrice !== null &&
    rule?.maxPrice !== ''
  ) {
    parts.push(`Price ≤ $${Number(rule.maxPrice)}`)
  }
  if (
    rule?.maxQuantity !== undefined &&
    rule?.maxQuantity !== null &&
    rule?.maxQuantity !== ''
  ) {
    parts.push(`Qty ≤ ${Number(rule.maxQuantity)}`)
  }
  if (rule?.source) {
    parts.push(ORDER_SOURCE_LABEL[rule.source] || rule.source)
  }
  if (rule?.paymentMethod) {
    parts.push(PAYMENT_TYPE_LABEL[rule.paymentMethod] || rule.paymentMethod)
  }
  if (rule?.productType) {
    parts.push(PRODUCT_TYPE_LABEL[rule.productType] || rule.productType)
  }

  return parts.length ? parts.join(' · ') : '—'
}

function ruleToForm(rule = {}) {
  return {
    name: rule.name ?? '',
    category: rule.category ?? FeeCategory.SERVICE,
    type: rule.type ?? FeeRuleType.FLAT,
    method: rule.method ?? FeeCalculationMethod.FIXED,
    value:
      rule.value !== undefined && rule.value !== null ? String(rule.value) : '',
    maxPrice:
      rule.maxPrice !== undefined && rule.maxPrice !== null
        ? String(rule.maxPrice)
        : '',
    maxQuantity:
      rule.maxQuantity !== undefined && rule.maxQuantity !== null
        ? String(rule.maxQuantity)
        : '',
    source: rule.source ?? '',
    paymentMethod: rule.paymentMethod ?? '',
    productType: rule.productType ?? '',
    exclusive: rule.exclusive !== false,
  }
}

function formToRule(form) {
  const value = Number(form.value)
  const rule = {
    category: form.category,
    type: form.type,
    method: form.method,
    value,
    exclusive: Boolean(form.exclusive),
  }

  if (form.name.trim()) {
    rule.name = form.name.trim()
  }

  if (form.type === FeeRuleType.PRICE) {
    rule.maxPrice = form.maxPrice.trim() === '' ? null : Number(form.maxPrice)
  }

  if (form.type === FeeRuleType.QUANTITY) {
    rule.maxQuantity =
      form.maxQuantity.trim() === '' ? null : Number(form.maxQuantity)
  }

  if (form.type === FeeRuleType.SOURCE && form.source) {
    rule.source = form.source
  }

  if (form.type === FeeRuleType.PAYMENT_METHOD && form.paymentMethod) {
    rule.paymentMethod = form.paymentMethod
  }

  if (form.type === FeeRuleType.PRODUCT_TYPE && form.productType) {
    rule.productType = form.productType
  }

  return rule
}

const syncOrgFeeConfigInStorage = (rules) => {
  const orgs = AuthService.getOrg() || []
  if (!orgs.length) return

  const next = orgs.map((org, index) =>
    index === 0
      ? {
          ...org,
          organization_fee_config: {
            ...(org.organization_fee_config || {}),
            rules,
          },
        }
      : org
  )

  AuthService.setOrg(next)
}

export default function OrganizationFeeConfigCard({ organizationUUID }) {
  const [rules, setRules] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [form, setForm] = useState(EMPTY_RULE_FORM)

  const loadFeeConfig = useCallback(async () => {
    if (!organizationUUID) return

    setIsLoading(true)
    setLoadError('')
    try {
      const response = await getOrganizationFeeConfig(organizationUUID)
      const payload = response?.data || response
      setRules(Array.isArray(payload?.rules) ? payload.rules : [])
    } catch (error) {
      console.error(error)
      setLoadError('Unable to load organization fee config.')
    } finally {
      setIsLoading(false)
    }
  }, [organizationUUID])

  useEffect(() => {
    loadFeeConfig()
  }, [loadFeeConfig])

  const sortedRules = useMemo(
    () =>
      rules.map((rule, index) => ({
        rule,
        index,
      })),
    [rules]
  )

  const valueNumber = Number(form.value)
  const canSubmit =
    form.category &&
    form.type &&
    form.method &&
    form.value.trim() !== '' &&
    Number.isFinite(valueNumber) &&
    (form.type !== FeeRuleType.SOURCE || Boolean(form.source)) &&
    (form.type !== FeeRuleType.PAYMENT_METHOD || Boolean(form.paymentMethod)) &&
    (form.type !== FeeRuleType.PRODUCT_TYPE || Boolean(form.productType))

  const persistRules = async (nextRules) => {
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await updateOrganizationFeeConfig(
        organizationUUID,
        nextRules
      )
      const payload = response?.data || response
      const savedRules = Array.isArray(payload?.rules)
        ? payload.rules
        : nextRules
      setRules(savedRules)
      syncOrgFeeConfigInStorage(savedRules)
    } catch (error) {
      console.error(error)
      setSaveError('Unable to save fee rules. Please try again.')
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const openAddModal = () => {
    setEditingIndex(null)
    setForm(EMPTY_RULE_FORM)
    setShowModal(true)
  }

  const openEditModal = (index) => {
    setEditingIndex(index)
    setForm(ruleToForm(rules[index]))
    setShowModal(true)
  }

  const closeModal = () => {
    if (!isSaving) {
      setShowModal(false)
      setEditingIndex(null)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit || isSaving) return

    const nextRule = formToRule(form)
    const nextRules =
      editingIndex === null
        ? [...rules, nextRule]
        : rules.map((rule, index) => (index === editingIndex ? nextRule : rule))

    try {
      await persistRules(nextRules)
      setShowModal(false)
      setEditingIndex(null)
      setForm(EMPTY_RULE_FORM)
    } catch {
      /* error surfaced via saveError */
    }
  }

  const handleDelete = async (index) => {
    const rule = rules[index]
    const label = rule?.name || `${rule?.category || 'fee'} rule`
    if (!window.confirm(`Delete “${label}”? This cannot be undone.`)) {
      return
    }

    const nextRules = rules.filter((_, ruleIndex) => ruleIndex !== index)
    try {
      await persistRules(nextRules)
    } catch {
      /* error surfaced via saveError */
    }
  }

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  return (
    <Card body className="card--sm mb-4">
      <div className="buyer-types-panel__toolbar buyer-types-panel__toolbar--custom mb-4">
        <div>
          <h2 className="h5 mb-1">Organization fee config</h2>
          <p className="subtitle subtitle--dark mb-0">
            Service, processing, and other platform fee rules for this
            organization.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={openAddModal}
          disabled={isLoading || isSaving}
        >
          + Add fee rule
        </Button>
      </div>

      {loadError && <p className="text-danger mb-3">{loadError}</p>}
      {saveError && <p className="text-danger mb-3">{saveError}</p>}

      {isLoading ? (
        <LoadingContainer />
      ) : rules.length === 0 ? (
        <p className="subtitle subtitle--dark mb-0">
          No fee rules configured yet. Add a rule to define how fees are
          calculated.
        </p>
      ) : (
        <div className="buyer-types-table-wrap">
          <table className="buyer-types-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Category</th>
                <th scope="col">Type</th>
                <th scope="col">Value</th>
                <th scope="col">Constraints</th>
                <th
                  scope="col"
                  className="buyer-types-table__actions"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {sortedRules.map(({ rule, index }) => (
                <tr key={`${rule.name || rule.category}-${index}`}>
                  <td className="buyer-types-table__name">
                    {rule.name || '—'}
                  </td>
                  <td>
                    {capitalizeString(
                      String(rule.category || '').replace(/_/g, ' ')
                    )}
                  </td>
                  <td>
                    {capitalizeString(
                      String(rule.type || '').replace(/_/g, ' ')
                    )}
                  </td>
                  <td>{formatRuleValue(rule)}</td>
                  <td>{formatRuleConstraints(rule)}</td>
                  <td className="buyer-types-table__actions">
                    <button
                      type="button"
                      className="buyer-types-table__icon-btn"
                      onClick={() => openEditModal(index)}
                      disabled={isSaving}
                      aria-label={`Edit ${rule.name || 'fee rule'}`}
                    >
                      <PencilIcon />
                    </button>
                    <button
                      type="button"
                      className="buyer-types-table__icon-btn"
                      onClick={() => handleDelete(index)}
                      disabled={isSaving}
                      aria-label={`Delete ${rule.name || 'fee rule'}`}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingIndex === null ? 'Add fee rule' : 'Edit fee rule'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="fee-rule-name">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(event) => updateForm('name', event.target.value)}
                    placeholder="Optional label"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="fee-rule-category">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={form.category}
                    onChange={(event) =>
                      updateForm('category', event.target.value)
                    }
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="fee-rule-type">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={form.type}
                    onChange={(event) => updateForm('type', event.target.value)}
                  >
                    {TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="fee-rule-method">
                  <Form.Label>Method</Form.Label>
                  <Form.Select
                    value={form.method}
                    onChange={(event) =>
                      updateForm('method', event.target.value)
                    }
                  >
                    {METHOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="fee-rule-value">
                  <Form.Label>Value</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.value}
                    onChange={(event) =>
                      updateForm('value', event.target.value)
                    }
                    required
                  />
                </Form.Group>
              </Col>
              {form.type === FeeRuleType.PRICE && (
                <Col md={6}>
                  <Form.Group controlId="fee-rule-max-price">
                    <Form.Label>Max price</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.maxPrice}
                      onChange={(event) =>
                        updateForm('maxPrice', event.target.value)
                      }
                      placeholder="Leave blank for catch-all tier"
                    />
                  </Form.Group>
                </Col>
              )}
              {form.type === FeeRuleType.QUANTITY && (
                <Col md={6}>
                  <Form.Group controlId="fee-rule-max-quantity">
                    <Form.Label>Max quantity</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      value={form.maxQuantity}
                      onChange={(event) =>
                        updateForm('maxQuantity', event.target.value)
                      }
                      placeholder="Leave blank for catch-all tier"
                    />
                  </Form.Group>
                </Col>
              )}
              {form.type === FeeRuleType.SOURCE && (
                <Col md={6}>
                  <Form.Group controlId="fee-rule-source">
                    <Form.Label>Source</Form.Label>
                    <Form.Select
                      value={form.source}
                      onChange={(event) =>
                        updateForm('source', event.target.value)
                      }
                      required
                    >
                      <option value="">Select source</option>
                      {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              {form.type === FeeRuleType.PAYMENT_METHOD && (
                <Col md={6}>
                  <Form.Group controlId="fee-rule-payment-method">
                    <Form.Label>Payment method</Form.Label>
                    <Form.Select
                      value={form.paymentMethod}
                      onChange={(event) =>
                        updateForm('paymentMethod', event.target.value)
                      }
                      required
                    >
                      <option value="">Select payment method</option>
                      {PAYMENT_METHOD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              {form.type === FeeRuleType.PRODUCT_TYPE && (
                <Col md={6}>
                  <Form.Group controlId="fee-rule-product-type">
                    <Form.Label>Product type</Form.Label>
                    <Form.Select
                      value={form.productType}
                      onChange={(event) =>
                        updateForm('productType', event.target.value)
                      }
                      required
                    >
                      <option value="">Select product type</option>
                      {PRODUCT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="fee-rule-exclusive"
                  label="Stop processing further rules when this one matches"
                  checked={form.exclusive}
                  onChange={(event) =>
                    updateForm('exclusive', event.target.checked)
                  }
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-light"
              onClick={closeModal}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save rule'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  )
}
